/**
 * Valideer een boolean waarde
 * @param  {*}          value, de te valideren boolean waarde
 * @return {boolean}    true indien het een number is, anders false
 */
export function validateBoolean(value) {
    if (typeof value === 'number') {
        if (value === 0 || value === 1) {
            return true;
        }
    } else if (typeof value === 'string') {
        const char = value.charAt(0);
        if (char === 'j' || char === 'J' ||
            char === 'n' || char === 'N') {
            return true;
        }
    } else if (value === true || value === false) {
        return true;
    }
    return false;
}

/**
 * validate email addres.
 * Must include a '@' and a '.'
 * The '.' index must be larger than the '@' index
 * i.e.: me@home.org => valid
 *       me@home     => invalid
 *       me.is@home  => invalid
 * @param {*} email, email addres to validate
 * @return {bool} true if valid email address, false otherwise
 */
export function validateEmail(email) {
    if (email && email.length > 0) {
        const ixAtSign = email.indexOf('@');
        const ixLastDot = email.lastIndexOf('.');
        // a valid email addres has a '@' and a '.' after the '@'
        if (ixAtSign === -1 || ixLastDot === -1 || ixAtSign > ixLastDot) {
            return false;
        }
    }
    return true;
}

/**
* Valideer een numerieke waarde
* @param  {type}          value te valideren nummer
* @param  {type}           min description
* @param  {type}           max description
* @return {boolean}        true indien het een number is, anders false
*/
export function validateNumber(value, min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER) {
    const number = value * 1;
    if (typeof number === 'number' && !isNaN(number)) {
        if (number >= min && number <= max) {
            return true;
        }
    }
    return false;
}

/**
 * Valideert een datum string op 'dd-mm-yyyy' formaat
 * Waarden volgens <SYSDATE> format worden ook valide beschouwd indien allowSysdate: true
 * @param  {string} dateString de te valideren datum string
 * @param {boolean} allowSysdate indicates if a sysdate string format like <SYSDATE+100> should be valid
 * @return {boolean} True bij positieve validatie, anders false.
 */
export function validateDate(dateString, allowSysdate = false) {
    let valid = false;
    try {
        if (dateString && dateString.length > 0) {
            // Validation op SYSDATE format
            if (allowSysdate && isSysdateString(dateString)) {
                valid = isValidSysdateString(dateString);
            } else {
                // Validation on dd-mm-yyyy format
                valid = isValidDateString(dateString);
            }
        } else {
            // No value entered
            valid = true;
        }
    } catch (error) {
        // Validation failed if error is caught
        valid = false;
    }
    return valid;
}

/**
 * Valideert een datum string op 'dd-mm-yyyy' formaat
 * @param  {string} dateString de te valideren datum string
 * @return {boolean} True bij positieve validatie, anders false.
 */
function isValidDateString(dateString) {
    let valid = false;

    // Split dateString in 3 parts
    const tokens = dateString.split('-');
    let day = tokens[0];
    let month = tokens[1];
    const year = tokens[2];
    // Add leading 0 if needed
    if (day.length === 1) {
        day = `0${day}`;
    }
    if (month.length === 1) {
        month = `0${month}`;
    }
    // eerste validatie met een date object
    const date = new Date(`${month}/${day}/${year}`);
    valid = date.toString() !== 'Invalid Date';
    // if date is valid, but day or month length is larger than 2, it has leading 0's
    // Return false when too many leading 0's are present, this is not caught in Date validation
    // controleer ook of de dag en maan van de date gelijk aan de invloer is.
    // verkrijg de dag en maand uit het date object
    const dateDay = date.getDate();
    const dateMonth = date.getMonth() + 1;  // januari = maand 0 (nul), tel er 1 bij op
    // tweede validatie
    if (valid) {
        if (parseInt(day) !== dateDay ||
            parseInt(month) !== dateMonth ||
            day.length > 2 ||
            month.length > 2 ||
            year.length < 4 ||
            year.length > 4) {
            // dan is het invalide
            valid = false;
        }
    }
    return valid;
}

/**
 * Valideert een tijd string op 'HH-MM-SS' formaat
 * @param  {string} timeString de te valideren tijd string
 * @return {boolean} True bij positieve validatie, anders false.
 */
function validateTime(timeString) {
    let valid = false;
    try {
        if (timeString && timeString.length > 0) {
            // split timeString in 3 parts
            const tokens = timeString.split(':');
            if (tokens.length === 3) {
                // formaat: HH:MM:SS
                if (tokens[0].length === 2 &&
                    tokens[1].length === 2 &&
                    tokens[2].length === 2) {
                    // bepaal de numerike waarden
                    const hours = tokens[0] * 1;
                    const minutes = tokens[1] * 1;
                    const seconds = tokens[2] * 1;
                    // zijn de waarde valide
                    valid = (hours > -1 && hours < 24 && minutes > -1 && minutes < 60 && seconds > -1 && seconds < 60);
                }
            }
        } else {
            // nog geen waarde ingevoerd
            valid = true;
        }
    } catch (error) {
        // Validation failed if error is caught
        valid = false;
    }
    return valid;
}

/**
 * Valideert een datum string op het date 'dd-mm-yyyy' of het dateTime 'dd-mm-yyyy hh:mm:ss', 'dd-mm-yyyyThh:mm:ss' formaat
 * Waarden volgens <SYSDATE> format worden ook valide beschouwd
 * @param  {string} dateTimeString de te valideren datum string
 * @return {boolean} True bij positieve validatie, anders false.
 */
export function validateDateTime(dateTimeString) {
    let valid = false;
    try {
        if (dateTimeString && dateTimeString.length > 0) {
            // Validation op SYSDATE
            if (isSysdateString(dateTimeString)) {
                valid = isValidSysdateString(dateTimeString);
            } else {
                // Validation on dd-mm-yyyy format
                valid = isValidDateTimeString(dateTimeString);
            }
        } else {
            // nog geen waarde ingevoerd
            valid = true;
        }
    } catch (error) {
        // Validation failed if error is caught
        valid = false;
    }
    return valid;
}

/**
 * Valideert een datum string op het date 'dd-mm-yyyy' of het dateTime 'dd-mm-yyyy hh:mm:ss', 'dd-mm-yyyyThh:mm:ss' formaat
 * @param  {string} dateTimeString de te valideren datum string
 * @return {boolean} True bij positieve validatie, anders false.
 */
function isValidDateTimeString(dateTimeString) {
    let valid = false;

    // scheidingsteken tussen datum en tijd: spatie of karakter 'T'
    let timeDevider = ' ';
    const dateTimeStringUC = dateTimeString.toUpperCase();
    if (dateTimeStringUC.includes('T')) {
        timeDevider = 'T';
    }
    // controle op datum tijd
    const dateTimeTokens = dateTimeStringUC.split(timeDevider);
    if (dateTimeTokens && dateTimeTokens.length === 2) {
        // valideer op datum en tijd
        valid = validateDate(dateTimeTokens[0], true) && validateTime(dateTimeTokens[1]);
    } else {
        // valideer enkel op datum
        valid = validateDate(dateTimeString, true);
    }

    return valid;
}

/**
 * Checks if the given string is formatted as a SYSDATE date-string, i.e. <SYSDATE-100>
 * @param {any} dateTimeString the input datetime string
 * @returns {boolean} true if dateFormat seems to be formatted as a SYSDATE date-string
 */
function isSysdateString(dateTimeString) {
    const dateTimeStringUC = dateTimeString.toUpperCase();
    return dateTimeStringUC.includes('<SYSDATE');
}

/**
 * Checks if the given string is formatted as a valid SYSDATE date-string, i.e.:
 * <SYSDATE> or
 * <SYSDATE-XXX> or
 * <SYSDATE+XXX>
 * @param {any} dateTimeString the input datetime string
 * @returns {boolean} true if dateFormat is a valid SYSDATE formatted string
 */
function isValidSysdateString(dateTimeString) {
    const dateTimeStringUC = dateTimeString.toUpperCase();
    // The sysdate-string is valid when it is formatted according to the regulra expression
    // Valid: <SYSDATE> or <SYSDATE-XXX> or <SYSDATE+XXX>
    const sysdateRegex = /^<SYSDATE([-+]+[\d]+)?\>$/;
    var expression = new RegExp(sysdateRegex);
    return expression.exec(dateTimeStringUC) !== null;
}

/**
 * Valideert de naam op dubbelingen binnen een themalaag.
 * @param  {string} filterName naam van het filter
 * @param  {object} themeLayer object
 * @return {boolean} True bij positieve validatie, anders false.
 */
export function validateFilterNameUnique(filterName, themeLayer) {
    let count = 0;
    for (const filter of themeLayer.filters) {
        if (filter.name && filterName && filter.name.toLowerCase() === filterName.toLowerCase()) {
            count++;
        }
    }
    return (count === 1);
}

/**
 * Validates string if it matches any word character, including underscores.
 * String must also consist of at least 3 characters.
 * @param {any} value
 * @return {boolean} True on positive validation, false otherwise.
 */
export function validateStringForWordCharactersAndAtLeast3Chars(value) {
    const regexValidation = /^\w{3,}$/;
    return (value && value.length > 0) ? regexValidation.test(value) : false;
}

/**
 * Validates string if it matches any word character, including underscores.
 * String must also start with a letter or underscore.
 * @param {any} value
 * @return {boolean} True on positive validation, false otherwise.
 */
export function validateSqlName(value) {
    return value.match(/^[a-zA-Z_]\w*$/) !== null;
}

export function validateUrl(value){
    try {
        const newUrl = new URL(value);
        return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch (err) {
        return false;
    }
}
